import styles from "./TextInput.module.scss";
import { forwardRef, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Title2, Title3 } from "@components/new/Typo";
import {
  LinkButton,
  LinkButtonSize,
  LinkButtonProps,
} from "@components/new/LinkButton";
import { Country } from "react-phone-number-input/input";
import { formatNumber } from "@utils/formatValues";
import cn from "classnames";
import YClose from "@assets/icons/outline/eye-off.svg?react";
import YOpen from "@assets/icons/outline/eye.svg?react";
import "react-phone-number-input/style.css";
import CountrySelect from "./CountrySelect";
import PhoneInput from "react-phone-number-input";
import ru from "react-phone-number-input/locale/ru";
import en from "react-phone-number-input/locale/en";
import tr from "react-phone-number-input/locale/tr";
import chinese from "./cn.json";
import { useProfile } from "@contexts/ProfileContext";
import { Tooltip } from "@components/new/Tooltip";

interface TextInputProps {
  className?: string;
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onClick?: (e: any) => void;
  onKeyDown?: (e: any) => void;
  hasError?: boolean;
  errorMessage?: string;
  name?: string;
  title?: ReactNode;
  type?: "text" | "password" | "phone" | "email" | "number";
  disabled?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
  disabledMessage?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  withWideSuffix?: boolean;
  onlyCountry?: string | undefined;
  description?: string;
  button?: LinkButtonProps;
  alignValue?: "left" | "center" | "right";
  required?: boolean;
  inputMode?: "text" | "email" | "search" | "tel" | "url" | "none" | "numeric" | "decimal" | undefined;
  wrapSuffix?: boolean;
}

export const preparePhoneString = (s: string) =>
  s[0] === "+" ? s : s && `+${s}`;

const TooltipContainer = ({
  disabledMessage,
  children,
}: {
  disabledMessage?: string;
  children?: string | JSX.Element | JSX.Element[];
}) => {
  if (disabledMessage) {
    return (
      <Tooltip action={<>{children}</>} position="top" text={disabledMessage} />
    );
  }
  return <>{children}</>;
};

const TextInput = forwardRef<HTMLInputElement, TextInputProps>((props, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isShowPassword, setIsShowPassword] = useState(false);
  const isFilled = props.value && props.value.length > 0;
  const isPhone = props.type === "phone";

  const { i18n } = useTranslation();
  const { account } = useProfile();
  const lang = i18n.language.toLocaleUpperCase();
  const locale =
    {
      EN: en,
      RU: ru,
      TR: tr,
      CN: chinese,
    }[lang] || en;

  const handleFocus = () => {
    setIsFocused(true);
    if (props.onFocus) {
      props.onFocus();
    }
  };
  const handleBlur = () => {
    setIsFocused(false);
    if (props.onBlur) {
      props.onBlur();
    }
  };

  const showError = props.hasError && props.errorMessage;
  const showFooter = showError || props.description || props.button;

  return (
    <div
      className={cn(
        props.className,
        styles.container,
        (isFocused || isFilled || isPhone) && styles.active,
        props.hasError && styles.error,
        isFocused && styles.focus,
        props.disabled && styles.disabled,
        styles[`align-${props.alignValue ?? "left"}`],
        props.withWideSuffix && styles.withWideSuffix,
        props.wrapSuffix && styles.wrapSuffix,
      )}
    >
      {props.title ? (
        <Title2 className={styles.title}>
          {props.title}
          {props.required ? <Title2 color="error-bright">*</Title2> : null}
        </Title2>
      ) : null}
      <div
        className={cn(styles.inputWrapper, isPhone && styles.inputWrapperPhone)}
      >
        <TooltipContainer
          disabledMessage={(props.disabled && props.disabledMessage) || ""}
        >
          {isPhone ? (
            <PhoneInput
              addInternationalOption={false}
              className={styles.input}
              {...(props.onlyCountry
                ? { countries: [props.onlyCountry as Country] }
                : {})}
              countryOptionsOrder={
                props.onlyCountry
                  ? [props.onlyCountry as Country]
                  : ["RU", "CN", "TR", "|", "..."]
              }
              countrySelectComponent={CountrySelect}
              {...(props.onlyCountry
                ? { defaultCountry: props.onlyCountry as Country }
                : {})}
              disabled={props.disabled}
              international
              labels={locale}
              limitMaxLength
              onChange={(value) => props.onChange?.(value || "")}
              value={preparePhoneString(props.value || "")}
            />
          ) : (
            <div
              className={styles.wrapper}
              onClick={props.onClick}
            >
              {props.prefix ? (
                <div className={styles.prefix}>{props.prefix}</div>
              ) : null}
              <input
                ref={ref}
                value={(props.value && props.type === "number")
                  ? (formatNumber(
                    typeof props.value === "string" ? (+props.value.replace(/\D/g, '')) : props.value
                  ))
                  : props.value
                }
                placeholder={props.placeholder}
                className={styles.input}
                  type={isShowPassword ? "text" : (
                    (props.type === "number" ? "text" : props.type) || "text"
                  )}
                inputMode={props.inputMode ?? (props.type === "number" ? "numeric" : undefined)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                name={props.name}
                onChange={(e) => {
                  if (props.onChange) {
                    let newValue = e.target.value;
                    if (newValue && props.type === "number") {
                      newValue = newValue.replace(/\D/g, '');
                      const newValueNumber = +newValue;
                      if (
                        props.min !== undefined &&
                        newValueNumber < props.min
                      ) {
                        newValue = String(props.min);
                      }
                      if (
                        props.max !== undefined &&
                        newValueNumber > props.max
                      ) {
                        newValue = String(props.max);
                      } else if (props.maxLength) {
                        const maxValue = 10 ** props.maxLength - 1;
                        if (maxValue < newValueNumber) {
                          newValue = String(maxValue);
                        }
                      }
                    }

                    props.onChange(newValue);
                  }
                }}
                onKeyDown={props.onKeyDown}
                disabled={props.disabled}
                maxLength={props.maxLength}
                min={props.min}
              />
              {props.name === "amountRU" && (
                <span className={styles.currency}>₽</span>
              )}
              {props.name === "amount" && (
                <span className="currency-icon">
                  {account?.region === "CN" && "¥"}
                  {account?.region === "TR" && "$"}
                </span>
              )}
              {props.suffix ? (
                <div className={styles.suffix}>{props.suffix}</div>
              ) : null}
              {props.type === "password" ? (
                isShowPassword ? (
                  <YOpen
                    className={styles.icon}
                    onClick={() => setIsShowPassword(!isShowPassword)}
                  />
                ) : (
                  <YClose
                    className={styles.icon}
                    onClick={() => setIsShowPassword(!isShowPassword)}
                  />
                )
              ) : null}
            </div>
          )}
        </TooltipContainer>
      </div>
      {showFooter ? (
        <div className={styles.footer}>
          {showError ? (
            <Title3 color="error-sade">{props.errorMessage}</Title3>
          ) : props.description ? (
            <Title3>{props.description}</Title3>
          ) : null}
          {props.button ? (
            <LinkButton
              size={LinkButtonSize.L}
              {...props.button}
              className={styles.footerButton}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
});

export default TextInput;
